import React, { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
    Plus, Trash2, Download, Loader2, Filter, X, Palette,
    FileSpreadsheet,
} from "lucide-react";
import TopNav from "../components/TopNav";
import {
    listPayables,
    createPayable,
    updatePayable,
    deletePayable,
    listPayableSites,
    listPayablesPalette,
    downloadPayablesExcel,
    importPayablesExcel,
} from "../lib/api";

// iter-200 — Payables tracker. A tablet-friendly landscape grid for
// contractors to log vendor invoices as they come in.
//
// iter-217 — Overhaul from user feedback:
//   • Customer split into separate name + address inputs in one cell.
//   • Removed the Reconciled column.
//   • Added a per-row color picker (overrides the deterministic site
//     hash); falls back to the shared pastel palette.
//   • Added a "+ Add item" button in the Product column so one
//     payable can be broken into sub-items with their own amounts;
//     the row total becomes the sum of sub-items (invoice-breakdown
//     style). Excel export expands sub-items into bulleted lines.

const PALETTE_FALLBACK = [
    "FFE9B8", "C8E6C9", "BBDEFB", "F8BBD0", "D1C4E9", "FFF59D",
    "B2DFDB", "FFCCBC", "DCEDC8", "F0F4C3", "CFD8DC", "FFECB3",
];

const COLS = [
    { key: "customer_name",    label: "Customer",            widthClass: "w-[10%]" },
    { key: "customer_address", label: "Site Address",        widthClass: "w-[12%]" },
    { key: "invoice_no",       label: "Invoice #",           widthClass: "w-[7%]"  },
    { key: "amount",           label: "$ Amount",            widthClass: "w-[7%]"  },
    { key: "vendor",           label: "Vendor",              widthClass: "w-[9%]"  },
    { key: "product",          label: "Product / Service",   widthClass: "w-[18%]" },
    { key: "date",             label: "Date",                widthClass: "w-[8%]"  },
    { key: "notes",            label: "Notes / Source",      widthClass: "w-[12%]" },
    { key: "job_category",     label: "Job Category",        widthClass: "w-[9%]"  },
    { key: "expense_type",     label: "Type",                widthClass: "w-[5%]"  },
    { key: "color",            label: "Colour",              widthClass: "w-[3%]"  },
];

function todayIso() {
    return new Date().toISOString().slice(0, 10);
}

function hexWithHash(c) {
    if (!c) return "";
    return c.startsWith("#") ? c : `#${c}`;
}

function matchesPeriod(dateStr, period) {
    if (period === "all" || !period) return true;
    if (!dateStr) return false;
    const d = new Date(dateStr + "T00:00:00");
    const now = new Date();
    if (period === "week") {
        const dayOffset = (now.getDay() + 6) % 7;
        const start = new Date(now);
        start.setDate(now.getDate() - dayOffset);
        start.setHours(0, 0, 0, 0);
        return d >= start;
    }
    if (period === "month") {
        return (
            d.getFullYear() === now.getFullYear() &&
            d.getMonth() === now.getMonth()
        );
    }
    return true;
}

function emptyDraft() {
    return {
        date: todayIso(),
        vendor: "",
        product: "",
        invoice_no: "",
        amount: "",
        customer_name: "",
        customer_address: "",
        notes: "",
        job_category: "",
        expense_type: "",
        color: "",
        sub_items: [],
    };
}

// iter-227 — AI_FILLABLE_FIELDS + batch state were used by the AI
// vision invoice parser (now removed per user request).

export default function PayablesPage() {
    const [rows, setRows] = useState([]);
    const [sites, setSites] = useState([]);
    const [palette, setPalette] = useState(PALETTE_FALLBACK);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [period, setPeriod] = useState("all");
    // iter-219 — sort state (date / customer / address / colour)
    const [sortBy, setSortBy] = useState("date");
    const [draft, setDraft] = useState(null);
    const [savingIds, setSavingIds] = useState(new Set());
    // iter-223 — Excel bulk-import state
    const [excelImporting, setExcelImporting] = useState(false);
    const excelInputRef = useRef(null);
    // iter-226 — multi-select + fancy delete confirm.
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [pendingDelete, setPendingDelete] = useState(null); // null | { ids: string[] }
    const [deletingBulk, setDeletingBulk] = useState(false);
    // iter-223 — Tax rate for Job Summary (configurable; default 13% HST).
    const [taxRate, setTaxRate] = useState(() => {
        const saved = localStorage.getItem("payables.taxRate");
        return saved !== null ? Number(saved) : 13;
    });
    useEffect(() => {
        localStorage.setItem("payables.taxRate", String(taxRate));
    }, [taxRate]);

    const refresh = async () => {
        try {
            const [items, siteList, pal] = await Promise.all([
                listPayables(),
                listPayableSites(),
                listPayablesPalette().catch(() => PALETTE_FALLBACK),
            ]);
            setRows(items);
            setSites(siteList);
            if (Array.isArray(pal) && pal.length) setPalette(pal);
        } catch (e) {
            toast.error("Failed to load payables");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    const filteredRows = useMemo(() => {
        const base = rows.filter((r) => matchesPeriod(r.date, period));
        if (sortBy === "date") return base; // server already returns date-desc
        const cmp = (a, b) => {
            if (sortBy === "customer_name") {
                return (a.customer_name || "").localeCompare(b.customer_name || "");
            }
            if (sortBy === "customer_address") {
                return (a.customer_address || "").localeCompare(b.customer_address || "");
            }
            if (sortBy === "color") {
                return (a.color || "").localeCompare(b.color || "");
            }
            return 0;
        };
        return [...base].sort(cmp);
    }, [rows, period, sortBy]);

    const totals = useMemo(() => {
        const total = filteredRows.reduce((s, r) => s + (r.amount || 0), 0);
        return { total, count: filteredRows.length };
    }, [filteredRows]);

    // iter-223 — Job Summary groups. Mirrors the "Job Summary" sheet
    // in the user's reference workbook. Amounts in the sheet are
    // treated as HST-inclusive totals; subtotal is backed out.
    const jobSummary = useMemo(() => {
        const rate = Math.max(0, Number(taxRate) || 0) / 100;
        const groups = {};  // key -> { key, color, count, total }
        for (const r of filteredRows) {
            const key = (r.job_category || r.customer_name || r.site_location || "Uncategorised").trim() || "Uncategorised";
            if (!groups[key]) {
                groups[key] = { key, color: r.color || "FFFFFF", count: 0, total: 0 };
            }
            groups[key].count += 1;
            groups[key].total += Number(r.amount || 0);
        }
        const list = Object.values(groups).map((g) => {
            const total = Math.round(g.total * 100) / 100;
            const subtotal = Math.round((total / (1 + rate)) * 100) / 100;
            const tax = Math.round((total - subtotal) * 100) / 100;
            return { ...g, total, subtotal, tax };
        }).sort((a, b) => b.total - a.total);
        const grand = list.reduce((acc, g) => ({
            count: acc.count + g.count,
            subtotal: acc.subtotal + g.subtotal,
            tax: acc.tax + g.tax,
            total: acc.total + g.total,
        }), { count: 0, subtotal: 0, tax: 0, total: 0 });
        return { list, grand };
    }, [filteredRows, taxRate]);

    // iter-223 — distinct category + type values (for autocomplete).
    const categoryOptions = useMemo(() => {
        const s = new Set();
        rows.forEach((r) => { if (r.job_category) s.add(r.job_category); });
        return Array.from(s).sort();
    }, [rows]);
    const typeOptions = useMemo(() => {
        const s = new Set();
        rows.forEach((r) => { if (r.expense_type) s.add(r.expense_type); });
        return Array.from(s).sort();
    }, [rows]);

    const startNewRow = () => setDraft(emptyDraft());

    const saveDraft = async () => {
        if (!draft) return;
        const nothingTyped = (
            !draft.customer_name.trim() &&
            !draft.customer_address.trim() &&
            !draft.vendor.trim() &&
            !draft.product.trim() &&
            !draft.invoice_no.trim() &&
            !Number(draft.amount) &&
            (draft.sub_items || []).every(
                (s) => !s.description && !Number(s.amount),
            )
        );
        if (nothingTyped) {
            toast.error("Enter at least one field before saving");
            return;
        }
        try {
            await createPayable({
                ...draft,
                amount: Number(draft.amount) || 0,
                sub_items: (draft.sub_items || [])
                    .map((s) => ({
                        description: s.description || "",
                        amount: Number(s.amount) || 0,
                    }))
                    .filter((s) => s.description || s.amount),
                invoice_files: draft.invoice_files || [],
                invoice_file: draft.invoice_file || null,
            });
            setDraft(null);
            await refresh();
            toast.success("Payable added");
        } catch (e) {
            toast.error("Failed to save row");
        }
    };

    const patchRow = async (id, patch) => {
        setSavingIds((s) => new Set(s).add(id));
        try {
            const updated = await updatePayable(id, patch);
            setRows((prev) => prev.map((r) => (r.id === id ? updated : r)));
        } catch (e) {
            toast.error("Save failed");
        } finally {
            setSavingIds((s) => {
                const n = new Set(s);
                n.delete(id);
                return n;
            });
        }
    };

    const removeRow = (id) => {
        // iter-226 — Fancy confirm modal (replaces window.confirm).
        setPendingDelete({ ids: [id] });
    };

    const confirmDeletePending = async () => {
        if (!pendingDelete?.ids?.length) {
            setPendingDelete(null);
            return;
        }
        const ids = pendingDelete.ids;
        setDeletingBulk(true);
        try {
            // Fire deletes in parallel but tolerate individual failures.
            const results = await Promise.allSettled(ids.map((id) => deletePayable(id)));
            const okIds = new Set();
            let failures = 0;
            results.forEach((r, i) => {
                if (r.status === "fulfilled") okIds.add(ids[i]);
                else failures += 1;
            });
            if (okIds.size) {
                setRows((prev) => prev.filter((r) => !okIds.has(r.id)));
                setSelectedIds((prev) => {
                    const n = new Set(prev);
                    okIds.forEach((id) => n.delete(id));
                    return n;
                });
            }
            if (failures === 0) {
                toast.success(okIds.size === 1 ? "Deleted" : `Deleted ${okIds.size} rows`);
            } else if (okIds.size === 0) {
                toast.error("Delete failed");
            } else {
                toast.error(`Deleted ${okIds.size}; ${failures} failed`);
            }
        } finally {
            setDeletingBulk(false);
            setPendingDelete(null);
        }
    };

    // iter-226 — bulk-select state + handlers.
    const toggleSelected = (id) => {
        setSelectedIds((prev) => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };
    const clearSelection = () => setSelectedIds(new Set());
    const bulkDelete = () => {
        if (selectedIds.size === 0) return;
        setPendingDelete({ ids: Array.from(selectedIds) });
    };

    const doExport = async () => {
        setExporting(true);
        try {
            await downloadPayablesExcel();
            toast.success("Excel downloaded");
        } catch (e) {
            toast.error("Export failed");
        } finally {
            setExporting(false);
        }
    };

    // iter-218 — Batch Import: iterate sequentially over N selected
    // files, call /import-one for each, show live progress, surface a
    // per-file summary at the end. Sequential (not Promise.all) so AI
    // rate limits stay sane and the user sees progress incrementally.
    // iter-227 — Batch import of invoice photos removed per user
    // request. Excel import is the only bulk-ingest path now.

    // iter-223 — Bulk-import payables from an .xlsx workbook. Preserves
    // the per-row fill colour from the Expenses sheet so the UI mirrors
    // whatever the user coded in Excel.
    const doExcelImport = async (file) => {
        if (!file) return;
        setExcelImporting(true);
        try {
            const res = await importPayablesExcel(file);
            toast.success(
                `Imported ${res.created} row${res.created === 1 ? "" : "s"}` +
                (res.skipped ? ` · skipped ${res.skipped} empty` : "")
            );
            await refresh();
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || "Import failed";
            toast.error(detail);
        } finally {
            setExcelImporting(false);
            if (excelInputRef.current) excelInputRef.current.value = "";
        }
    };

    return (
        <div className="h-dvh bg-[#0A1128] text-slate-100 flex flex-col">
            <TopNav active="payables" />
            {/* iter-227 — Single sheet-style grid at every width. The
                old mobile card fallback (<768px -> display:block) made
                the Payables table look nothing like the reference
                Excel sheet on tablet/phone. We now keep the real table
                layout and let the outer wrapper scroll horizontally;
                the dark-blue header + colour tinting + Entry # column
                stay identical to the Excel export. */}
            <style>{`
                .payables-table input,
                .payables-table [type="date"],
                .payables-table [type="number"] {
                    font-size: 13px;
                }
                @media (max-width: 767px) {
                    .payables-table input,
                    .payables-table [type="date"],
                    .payables-table [type="number"] {
                        font-size: 15px !important;
                        padding: 6px 8px !important;
                    }
                }
            `}</style>
            <div className="flex-1 min-h-0 overflow-y-auto max-w-[1600px] w-full mx-auto px-3 md:px-8 py-4 md:py-6 pb-24">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold text-white">
                            Payables
                        </h1>
                        <p className="text-sm text-slate-400 mt-1">
                            Log vendor invoices as they come in. Pick a row colour,
                            break a single invoice into sub-items, then export a
                            colour-coded Excel for monthly reconciliation.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {/* iter-223 — Excel bulk import */}
                        <input
                            ref={excelInputRef}
                            type="file"
                            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            className="hidden"
                            onChange={(e) => doExcelImport(e.target.files?.[0])}
                            data-testid="payables-excel-import-input"
                        />
                        <button
                            onClick={() => excelInputRef.current?.click()}
                            disabled={excelImporting}
                            className="flex items-center gap-2 px-4 py-2 rounded-md bg-sky-500 hover:bg-sky-400 text-white font-semibold text-sm disabled:opacity-50"
                            title="Upload an .xlsx workbook — row fill colours are preserved"
                            data-testid="payables-excel-import-btn"
                        >
                            {excelImporting
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <FileSpreadsheet className="w-4 h-4" />}
                            Import Excel
                        </button>
                        <button
                            onClick={doExport}
                            disabled={exporting}
                            className="flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-sm disabled:opacity-50"
                            data-testid="payables-export-btn"
                        >
                            {exporting
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Download className="w-4 h-4" />}
                            Export Excel
                        </button>
                        <button
                            onClick={startNewRow}
                            className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#00A3FF] hover:bg-[#1DB2FF] text-white font-semibold text-sm"
                            data-testid="payables-add-btn"
                        >
                            <Plus className="w-4 h-4" />
                            Add Row
                        </button>
                        {/* iter-228 — Select All toggle (phone-friendly:
                            works without horizontally scrolling to the
                            thead checkbox). Toggles between "select
                            every visible row" and "clear selection". */}
                        {filteredRows.length > 0 && (
                            <button
                                onClick={() => {
                                    const allSelected = filteredRows.every((r) => selectedIds.has(r.id));
                                    if (allSelected) {
                                        clearSelection();
                                    } else {
                                        setSelectedIds(new Set(filteredRows.map((r) => r.id)));
                                    }
                                }}
                                className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#1C253C] hover:bg-[#223055] text-slate-200 font-semibold text-sm border border-[#2A3655]"
                                data-testid="payables-select-all-btn"
                            >
                                {filteredRows.every((r) => selectedIds.has(r.id)) && filteredRows.length > 0
                                    ? "Clear selection"
                                    : `Select all (${filteredRows.length})`}
                            </button>
                        )}
                        {/* iter-226 — Bulk delete appears only when ≥1 row selected. */}
                        {selectedIds.size > 0 && (
                            <button
                                onClick={bulkDelete}
                                className="flex items-center gap-2 px-4 py-2 rounded-md bg-red-600 hover:bg-red-500 text-white font-semibold text-sm"
                                data-testid="payables-bulk-delete-btn"
                            >
                                <Trash2 className="w-4 h-4" />
                                Delete {selectedIds.size} selected
                            </button>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                    <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-slate-400" />
                            <span className="text-xs uppercase tracking-wide text-slate-400 mr-2">
                                Period
                            </span>
                            {[
                                { k: "week",  label: "This Week" },
                                { k: "month", label: "This Month" },
                                { k: "all",   label: "All" },
                            ].map((p) => (
                                <button
                                    key={p.k}
                                    onClick={() => setPeriod(p.k)}
                                    data-testid={`payables-filter-${p.k}`}
                                    className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                                        period === p.k
                                            ? "bg-[#00A3FF] text-white"
                                            : "bg-[#1C253C] text-slate-300 hover:bg-[#223055]"
                                    }`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        {/* iter-219 — sort selector */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs uppercase tracking-wide text-slate-400 mr-1">
                                Sort by
                            </span>
                            {[
                                { k: "date",             label: "Date" },
                                { k: "customer_name",    label: "Customer" },
                                { k: "customer_address", label: "Address" },
                                { k: "color",            label: "Colour" },
                            ].map((s) => (
                                <button
                                    key={s.k}
                                    onClick={() => setSortBy(s.k)}
                                    data-testid={`payables-sort-${s.k}`}
                                    className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                                        sortBy === s.k
                                            ? "bg-amber-400 text-black"
                                            : "bg-[#1C253C] text-slate-300 hover:bg-[#223055]"
                                    }`}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                        <div>
                            <span className="text-slate-400 mr-1.5">Rows:</span>
                            <span className="font-semibold text-white" data-testid="payables-count">
                                {totals.count}
                            </span>
                        </div>
                        <div>
                            <span className="text-slate-400 mr-1.5">Total:</span>
                            <span className="font-semibold text-emerald-300" data-testid="payables-grand-total">
                                ${totals.total.toFixed(2)}
                            </span>
                        </div>
                    </div>
                </div>

                <datalist id="payables-customer-name-list">
                    {sites.map((s) => (
                        <option key={s.name} value={s.name} />
                    ))}
                </datalist>
                <datalist id="payables-customer-address-list">
                    {sites
                        .filter((s) => s.address)
                        .map((s) => (
                            <option key={`${s.name}-addr`} value={s.address} />
                        ))}
                </datalist>
                {/* iter-223 — Autocomplete for category + type */}
                <datalist id="payables-job-category-list">
                    {categoryOptions.map((c) => (
                        <option key={c} value={c} />
                    ))}
                </datalist>
                <datalist id="payables-expense-type-list">
                    {typeOptions.map((t) => (
                        <option key={t} value={t} />
                    ))}
                </datalist>

                {/* iter-223 — Job Summary (per-category subtotal / HST / total). */}
                {jobSummary.list.length > 0 && (
                    <JobSummaryPanel
                        summary={jobSummary}
                        taxRate={taxRate}
                        setTaxRate={setTaxRate}
                    />
                )}

                {/* iter-225 — Mirrors the "Expenses" sheet exactly:
                    big section title, dark-blue 4472C4 header row,
                    full-row tinting by category colour, Entry # as
                    column 1. Delete / colour-picker / add-item live
                    in the trailing actions cell so the data columns
                    stay the ones the user sees in Excel. */}
                <div className="rounded-md border border-slate-500 bg-white overflow-hidden shadow-lg">
                    <div className="px-4 pt-3 pb-2 bg-white border-b border-slate-200">
                        <h3 className="text-lg font-bold text-slate-900 leading-tight">
                            Expenses
                        </h3>
                        <p className="text-[11px] text-slate-600 italic mt-0.5">
                            Every row kept in the exact order of the reference workbook — same columns, same colours.
                        </p>
                    </div>
                    <div className="overflow-x-auto">
                        <table
                            className="w-full text-sm payables-table border-collapse"
                            data-testid="payables-table"
                            style={{ fontFamily: "'Calibri', 'Segoe UI', system-ui, sans-serif" }}
                        >
                            <thead>
                                <tr style={{ background: "#4472C4", color: "#FFFFFF" }}>
                                    {/* iter-226 — Select-all checkbox */}
                                    <th className="w-10 px-2 py-2.5 border border-slate-300 text-center">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 cursor-pointer accent-sky-600"
                                            checked={filteredRows.length > 0 && filteredRows.every((r) => selectedIds.has(r.id))}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedIds(new Set(filteredRows.map((r) => r.id)));
                                                } else {
                                                    clearSelection();
                                                }
                                            }}
                                            title="Select all visible rows"
                                            data-testid="payables-select-all"
                                        />
                                    </th>
                                    <th className="text-left font-bold text-xs uppercase px-2 py-2.5 border border-slate-300 w-12">Entry #</th>
                                    {COLS.map((c) => (
                                        <th
                                            key={c.key}
                                            className={`text-left font-bold text-xs uppercase px-3 py-2.5 border border-slate-300 ${c.widthClass}`}
                                        >
                                            {c.label}
                                        </th>
                                    ))}
                                    <th className="w-[2%] px-2 py-2.5 border border-slate-300" />
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan={COLS.length + 3} className="px-4 py-10 text-center text-slate-500 bg-white">
                                            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                                            Loading payables…
                                        </td>
                                    </tr>
                                )}

                                {draft && (
                                    <PayableEditableRow
                                        isDraft
                                        entryNo={filteredRows.length + 1}
                                        row={draft}
                                        palette={palette}
                                        onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
                                        onSave={saveDraft}
                                        onCancel={() => setDraft(null)}
                                    />
                                )}

                                {!loading && filteredRows.length === 0 && !draft && (
                                    <tr>
                                        <td colSpan={COLS.length + 3} className="px-4 py-10 text-center text-slate-500 bg-white">
                                            No payables yet. Click <b>Add Row</b> or <b>Import Excel</b> to log your first vendor invoice.
                                        </td>
                                    </tr>
                                )}

                                {!loading && filteredRows.map((r, idx) => (
                                    <PayableEditableRow
                                        key={r.id}
                                        entryNo={idx + 1}
                                        row={r}
                                        palette={palette}
                                        saving={savingIds.has(r.id)}
                                        selected={selectedIds.has(r.id)}
                                        onToggleSelect={() => toggleSelected(r.id)}
                                        onPatch={(patch) => patchRow(r.id, patch)}
                                        onReplace={(updated) => {
                                            setRows((prev) =>
                                                prev.map((x) => x.id === r.id ? updated : x),
                                            );
                                        }}
                                        onDelete={() => removeRow(r.id)}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* iter-224 — Legend only shows customers with at
                    least one payable. Project titles still populate
                    the datalists above but no longer linger in the
                    colour legend after a user deletes every row. */}
                {sites.filter((s) => s.has_payable).length > 0 && (
                    <div className="mt-6">
                        <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">
                            Customer Colour Legend <span className="normal-case text-slate-500">(auto-coloured — override per row with the colour picker)</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {sites.filter((s) => s.has_payable).map((s) => (
                                <div
                                    key={s.name}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs text-slate-900 font-medium"
                                    style={{ background: hexWithHash(s.color) }}
                                    data-testid={`payables-legend-${s.name}`}
                                >
                                    {s.name}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {/* iter-228 — Floating Save/Cancel bar for the draft row.
                Pinned to the bottom of the viewport so the user never
                has to horizontally scroll the Excel grid to find the
                Save button on a phone. */}
            {draft && (
                <div
                    className="fixed bottom-0 left-0 right-0 z-[90] bg-[#0F1B3A] border-t-2 border-sky-500 shadow-[0_-6px_18px_rgba(0,0,0,0.4)] px-4 py-3 flex items-center justify-between gap-3"
                    data-testid="payables-draft-save-bar"
                >
                    <div className="text-sm text-slate-200">
                        <div className="text-xs uppercase tracking-wider text-sky-300 font-semibold">New row</div>
                        <div className="text-xs text-slate-400 truncate max-w-[55vw]">
                            {draft.customer_name || draft.customer_address || draft.vendor || "Fill in the row above, then tap Save."}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setDraft(null)}
                            className="px-4 py-2 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm font-semibold"
                            data-testid="payables-draft-floating-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={saveDraft}
                            className="px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold"
                            data-testid="payables-draft-floating-save"
                        >
                            Save row
                        </button>
                    </div>
                </div>
            )}
            {/* iter-226 — Fancy delete confirm (handles both single + bulk). */}
            {pendingDelete && (
                <ConfirmDeleteModal
                    count={pendingDelete.ids.length}
                    busy={deletingBulk}
                    onConfirm={confirmDeletePending}
                    onCancel={() => !deletingBulk && setPendingDelete(null)}
                />
            )}
        </div>
    );
}

// ------- Row component (handles both draft & existing) -------

function PayableEditableRow({
    isDraft = false,
    entryNo,
    row,
    palette,
    saving = false,
    selected = false,
    onToggleSelect,
    onChange,
    onPatch,
    onReplace,
    onSave,
    onCancel,
    onDelete,
}) {
    const [local, setLocal] = useState(row);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerRect, setPickerRect] = useState(null); // {top, left} of button
    const pickerRef = useRef(null);
    const colorBtnRef = useRef(null);

    useEffect(() => { setLocal(row); }, [row.id, row.updated_at]);

    // Close color picker on outside click (ignore clicks inside the
    // picker OR on the toggle button so the toggle behaves cleanly).
    useEffect(() => {
        if (!pickerOpen) return;
        const h = (e) => {
            const insidePicker = pickerRef.current && pickerRef.current.contains(e.target);
            const onButton = colorBtnRef.current && colorBtnRef.current.contains(e.target);
            if (!insidePicker && !onButton) {
                setPickerOpen(false);
            }
        };
        window.addEventListener("mousedown", h);
        return () => window.removeEventListener("mousedown", h);
    }, [pickerOpen]);

    const bgHex = hexWithHash(local.color || "FFFFFF");
    // iter-225 — Even an un-tinted row gets a readable white bg so
    // the sheet-like look holds. Any explicit colour wins.
    const rowStyle =
        bgHex && bgHex.toUpperCase() !== "#FFFFFF"
            ? { background: bgHex, color: "#111827" }
            : { background: "#FFFFFF", color: "#111827" };

    // Sum of sub_items (if any) wins over user-typed amount.
    const subTotal = useMemo(() => {
        return (local.sub_items || [])
            .reduce((s, it) => s + (Number(it.amount) || 0), 0);
    }, [local.sub_items]);
    const hasSubs = (local.sub_items || []).length > 0;
    const effectiveAmount = hasSubs ? subTotal : local.amount;

    const applyChange = (patch) => {
        const next = { ...local, ...patch };
        setLocal(next);
        if (isDraft) onChange?.(patch);
    };

    const commit = (field, raw) => {
        if (isDraft) return;
        let val = raw;
        if (field === "amount") val = Number(raw) || 0;
        if (val === row[field]) return;
        onPatch?.({ [field]: val });
    };

    const commitSubItems = (next) => {
        if (isDraft) {
            applyChange({ sub_items: next });
            return;
        }
        onPatch?.({ sub_items: next });
    };

    const setSubItemLocal = (idx, patch) => {
        const next = (local.sub_items || []).map((s, i) =>
            i === idx ? { ...s, ...patch } : s,
        );
        setLocal({ ...local, sub_items: next });
        if (isDraft) applyChange({ sub_items: next });
    };

    const addSubItem = () => {
        const next = [...(local.sub_items || []), { description: "", amount: 0 }];
        setLocal({ ...local, sub_items: next });
        if (isDraft) applyChange({ sub_items: next });
        // NOTE: deliberately do NOT commit here. The backend's
        // _clean_subs() strips sub-items where description + amount
        // are both empty, so a server round-trip would wipe this
        // row out and nothing visible would happen (that's exactly
        // the bug the user reported). The row persists on the first
        // onBlur after the user types a description or amount.
    };

    const removeSubItem = (idx) => {
        const next = (local.sub_items || []).filter((_, i) => i !== idx);
        setLocal({ ...local, sub_items: next });
        commitSubItems(next);
    };

    const commitColor = (hex, opts = {}) => {
        const next = hex || "";
        setLocal({ ...local, color: next });
        if (isDraft) {
            applyChange({ color: next });
        } else {
            onPatch?.({ color: next });
        }
        if (!opts.keepOpen) setPickerOpen(false);
    };

    const commitAmount = (raw) => {
        if (hasSubs) return; // amount is derived; input disabled visually
        commit("amount", raw);
    };

    // iter-221 — Invoice files (plural). Upload adds a new file to
    // the invoice_files list on the row. For a draft row we hold
    // files in local state until Save; for an existing row we hit
    // iter-227 — Receipt attach / AI vision removed per user request.
    // The row no longer saves invoice files, so handleUpload /
    // handleImport / preview / detach are gone. "Missing-field"
    // highlighter is also retired (used to be lit after an AI parse).
    const missingFields = new Set();
    const clearMissing = () => {};
    const missingCls = () => "";

    const inputCls =
        "w-full bg-transparent outline-none focus:bg-white/80 rounded px-1.5 py-1 text-slate-900 placeholder:text-slate-500";
    const draftInputCls =
        "w-full bg-white/80 rounded px-1.5 py-1 outline-none focus:bg-white text-slate-900 placeholder:text-slate-500";
    const inp = isDraft ? draftInputCls : inputCls;

    return (
        <tr
            className="hover:brightness-105 transition align-top"
            style={rowStyle}
            data-testid={isDraft ? "payables-draft-row" : `payables-row-${row.id}`}
        >
            {/* iter-226 — Per-row checkbox (hidden for draft rows). */}
            <td
                className="px-2 py-1.5 border border-slate-300 text-center align-middle"
                data-label=""
            >
                {!isDraft && (
                    <input
                        type="checkbox"
                        checked={selected}
                        onChange={onToggleSelect}
                        className="w-4 h-4 cursor-pointer accent-sky-600"
                        aria-label="Select row"
                        data-testid={`payables-select-${row.id}`}
                    />
                )}
            </td>

            {/* iter-225 — Entry # column (matches Excel). Draft rows
                show a '+' so the user knows this slot becomes the
                next entry on save. */}
            <td
                className="px-2 py-1.5 border border-slate-300 text-center text-xs font-mono font-semibold text-slate-700"
                data-label="Entry #"
            >
                {isDraft ? <span className="text-sky-600">+</span> : entryNo}
            </td>

            {/* Customer name */}
            <td className="px-3 py-1.5 border border-slate-300" data-label="Customer">
                <input
                    list="payables-customer-name-list"
                    value={local.customer_name || ""}
                    onChange={(e) => { applyChange({ customer_name: e.target.value }); clearMissing("customer_name"); }}
                    onBlur={(e) => commit("customer_name", e.target.value.trim())}
                    placeholder={missingFields.has("customer_name") ? "⚠ Customer name" : "Customer"}
                    className={`w-full ${inp} font-medium ${missingCls("customer_name")}`}
                    data-testid={isDraft ? "payables-draft-customer-name" : "payables-cell-customer-name"}
                    autoFocus={isDraft}
                />
            </td>

            {/* Site address */}
            <td className="px-3 py-1.5 border border-slate-300" data-label="Site Address">
                <input
                    list="payables-customer-address-list"
                    value={local.customer_address || ""}
                    onChange={(e) => { applyChange({ customer_address: e.target.value }); clearMissing("customer_address"); }}
                    onBlur={(e) => commit("customer_address", e.target.value.trim())}
                    placeholder={missingFields.has("customer_address") ? "⚠ Site address" : "Site address"}
                    className={`w-full ${inp} text-sm ${missingCls("customer_address")}`}
                    data-testid={isDraft ? "payables-draft-customer-address" : "payables-cell-customer-address"}
                />
            </td>

            {/* Invoice # */}
            <td className="px-3 py-1.5 border border-slate-300" data-label="Invoice #">
                <input
                    value={local.invoice_no || ""}
                    onChange={(e) => { applyChange({ invoice_no: e.target.value }); clearMissing("invoice_no"); }}
                    onBlur={(e) => commit("invoice_no", e.target.value.trim())}
                    placeholder={missingFields.has("invoice_no") ? "⚠ INV-?" : "INV-123"}
                    className={`w-full ${inp} text-sm ${missingCls("invoice_no")}`}
                    data-testid={isDraft ? "payables-draft-invoice" : "payables-cell-invoice"}
                />
            </td>

            {/* Amount — always editable. If sub-items exist, show the
                computed subtotal as a tiny hint beneath the input so
                the user can see the breakdown total without losing
                control over the invoice-level amount. */}
            <td className="px-3 py-1.5 border border-slate-300" data-label="$ Amount">
                <div className="flex items-center">
                    <span className="font-mono text-sm mr-0.5">$</span>
                    <input
                        type="number"
                        step="0.01"
                        value={local.amount ?? ""}
                        onChange={(e) => { applyChange({ amount: e.target.value }); clearMissing("amount"); }}
                        onBlur={(e) => commit("amount", e.target.value)}
                        placeholder={missingFields.has("amount") ? "⚠ 0.00" : "0.00"}
                        className={`w-full ${inp} text-right font-mono text-sm ${missingCls("amount")}`}
                        data-testid={isDraft ? "payables-draft-amount" : "payables-cell-amount"}
                    />
                </div>
                {hasSubs && (
                    <div
                        className="text-[10px] text-slate-700/70 text-right mt-0.5 italic"
                        title="Sum of sub-items (independent from the invoice total)"
                        data-testid={isDraft ? "payables-draft-subtotal-hint" : "payables-cell-subtotal-hint"}
                    >
                        items ${subTotal.toFixed(2)}
                    </div>
                )}
            </td>

            {/* Vendor */}
            <td className="px-3 py-1.5 border border-slate-300" data-label="Vendor">
                <input
                    value={local.vendor || ""}
                    onChange={(e) => { applyChange({ vendor: e.target.value }); clearMissing("vendor"); }}
                    onBlur={(e) => commit("vendor", e.target.value.trim())}
                    placeholder={missingFields.has("vendor") ? "⚠ Vendor — fill in" : "Vendor name"}
                    className={`${inp} ${missingCls("vendor")}`}
                    data-testid={isDraft ? "payables-draft-vendor" : "payables-cell-vendor"}
                />
            </td>

            {/* Product + sub-items + invoice upload */}
            <td className="px-3 py-1.5 border border-slate-300" data-label="Product / Service">
                <input
                    value={local.product || ""}
                    onChange={(e) => { applyChange({ product: e.target.value }); clearMissing("product"); }}
                    onBlur={(e) => commit("product", e.target.value.trim())}
                    placeholder={missingFields.has("product") ? "⚠ Product / Service — fill in" : "Lumber / excavator rental / etc."}
                    className={`${inp} ${missingCls("product")}`}
                    data-testid={isDraft ? "payables-draft-product" : "payables-cell-product"}
                />
                {(local.sub_items || []).map((s, idx) => (
                    <div
                        key={idx}
                        className="mt-2 pl-2 border-l-2 border-slate-400/50"
                        data-testid={`payables-sub-item-${idx}`}
                    >
                        <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-600">
                                Item {idx + 1}
                            </span>
                            <button
                                onClick={() => removeSubItem(idx)}
                                className="p-0.5 rounded hover:bg-rose-500/20 text-rose-700"
                                data-testid={`payables-sub-item-${idx}-delete`}
                                aria-label="Remove sub-item"
                                title="Remove this sub-item"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                        <div className="flex gap-1.5 items-start">
                            <input
                                value={s.description || ""}
                                onChange={(e) => setSubItemLocal(idx, { description: e.target.value })}
                                onBlur={() => commitSubItems(local.sub_items)}
                                placeholder="Description"
                                className={`flex-1 min-w-0 ${inp} text-sm`}
                                data-testid={`payables-sub-item-${idx}-desc`}
                            />
                            <div className="flex items-center shrink-0">
                                <span className="font-mono text-xs mr-0.5">$</span>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={s.amount ?? ""}
                                    onChange={(e) => setSubItemLocal(idx, { amount: e.target.value })}
                                    onBlur={() =>
                                        commitSubItems(
                                            (local.sub_items || []).map((it) => ({
                                                description: it.description || "",
                                                amount: Number(it.amount) || 0,
                                            })),
                                        )
                                    }
                                    placeholder="0.00"
                                    className={`w-24 ${inp} text-right font-mono text-sm`}
                                    data-testid={`payables-sub-item-${idx}-amount`}
                                />
                            </div>
                        </div>
                    </div>
                ))}
                {hasSubs && (
                    <div
                        className="flex justify-end mt-1.5 pr-5 text-xs font-semibold"
                        data-testid={isDraft ? "payables-draft-subtotal" : "payables-cell-subtotal"}
                    >
                        Subtotal: ${subTotal.toFixed(2)}
                    </div>
                )}
                <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
                    <button
                        onClick={addSubItem}
                        className="flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold text-slate-700 hover:text-black px-2 py-0.5 rounded hover:bg-white/40"
                        data-testid={isDraft ? "payables-draft-add-sub-item" : `payables-row-${row.id}-add-sub-item`}
                    >
                        <Plus className="w-3 h-3" />
                        Add item
                    </button>
                </div>
            </td>

            {/* Date */}
            <td className="px-3 py-1.5 border border-slate-300" data-label="Date">
                <input
                    type="date"
                    value={local.date || ""}
                    onChange={(e) => { applyChange({ date: e.target.value }); clearMissing("date"); }}
                    onBlur={(e) => commit("date", e.target.value)}
                    className={`${inp} ${missingCls("date")}`}
                    data-testid={isDraft ? "payables-draft-date" : "payables-cell-date"}
                />
            </td>

            {/* iter-223 — Notes / Source */}
            <td className="px-3 py-1.5 border border-slate-300" data-label="Notes / Source">
                <input
                    value={local.notes || ""}
                    onChange={(e) => applyChange({ notes: e.target.value })}
                    onBlur={(e) => commit("notes", e.target.value.trim())}
                    placeholder="PO #, HST #, delivery notes…"
                    className={inp}
                    data-testid={isDraft ? "payables-draft-notes" : "payables-cell-notes"}
                />
            </td>

            {/* iter-223 — Job Category (groups rows for Summary totals) */}
            <td className="px-3 py-1.5 border border-slate-300" data-label="Job Category">
                <input
                    value={local.job_category || ""}
                    onChange={(e) => applyChange({ job_category: e.target.value })}
                    onBlur={(e) => commit("job_category", e.target.value.trim())}
                    placeholder="e.g. 91 Georgian Manor"
                    className={inp}
                    list="payables-job-category-list"
                    data-testid={isDraft ? "payables-draft-job-category" : "payables-cell-job-category"}
                />
            </td>

            {/* iter-223 — Expense Type */}
            <td className="px-3 py-1.5 border border-slate-300" data-label="Type">
                <input
                    value={local.expense_type || ""}
                    onChange={(e) => applyChange({ expense_type: e.target.value })}
                    onBlur={(e) => commit("expense_type", e.target.value.trim())}
                    placeholder="Material"
                    className={inp}
                    list="payables-expense-type-list"
                    data-testid={isDraft ? "payables-draft-expense-type" : "payables-cell-expense-type"}
                />
            </td>

            {/* Colour picker */}
            <td className="px-3 py-1.5 border border-slate-300 text-center" data-label="Colour">
                <button
                    ref={colorBtnRef}
                    onClick={() => {
                        if (pickerOpen) { setPickerOpen(false); return; }
                        // Compute button rect so we can render the
                        // picker as position:fixed. This bypasses the
                        // table's overflow-x-auto + wrapper's
                        // overflow-hidden which otherwise clip the
                        // dropdown and swallow swatch clicks.
                        const r = colorBtnRef.current?.getBoundingClientRect();
                        if (r) {
                            setPickerRect({
                                top: Math.round(r.bottom + 4),
                                right: Math.round(window.innerWidth - r.right),
                            });
                        }
                        setPickerOpen(true);
                    }}
                    className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded border border-slate-500/40 hover:border-slate-700 bg-white/60"
                    title="Pick a colour for this row"
                    data-testid={isDraft ? "payables-draft-color-btn" : `payables-row-${row.id}-color-btn`}
                >
                    <span
                        className="inline-block w-4 h-4 rounded-sm border border-slate-400"
                        style={{ background: hexWithHash(local.color || "FFFFFF") }}
                    />
                    <Palette className="w-3.5 h-3.5 text-slate-700" />
                </button>
                {pickerOpen && pickerRect && createPortal(
                    <div
                        ref={pickerRef}
                        style={{
                            position: "fixed",
                            top: pickerRect.top,
                            right: pickerRect.right,
                            zIndex: 9999,
                        }}
                        className="w-52 bg-white rounded-md shadow-2xl border border-slate-300 p-2 grid grid-cols-4 gap-1.5"
                        data-testid={isDraft ? "payables-draft-color-palette" : `payables-row-${row.id}-color-palette`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* iter-222 — Full colour wheel + hex input. The
                            native <input type="color"> opens the OS wheel
                            on desktop and the native picker on iOS/Android,
                            giving unlimited colour choice beyond the pastel
                            quick-swatches below. */}
                        <div
                            className="col-span-4 flex items-center gap-2 pb-2 mb-1 border-b border-slate-200"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <label
                                className="relative inline-flex items-center justify-center w-9 h-9 rounded-md border-2 border-slate-300 cursor-pointer hover:border-slate-700 shrink-0 overflow-hidden"
                                title="Open full colour wheel"
                                style={{ background: hexWithHash(local.color || "FFFFFF") }}
                            >
                                <input
                                    type="color"
                                    value={hexWithHash((local.color || "FFFFFF").toUpperCase())}
                                    onChange={(e) => commitColor(e.target.value.replace(/^#/, "").toUpperCase(), { keepOpen: true })}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    data-testid={isDraft ? "payables-draft-color-wheel" : `payables-row-${row.id}-color-wheel`}
                                    aria-label="Open colour wheel"
                                />
                                <Palette className="w-4 h-4 text-slate-700 pointer-events-none mix-blend-difference" style={{ color: "white" }} />
                            </label>
                            <input
                                type="text"
                                maxLength={7}
                                value={hexWithHash((local.color || "").toUpperCase())}
                                onChange={(e) => {
                                    const raw = e.target.value.replace(/[^#0-9a-fA-F]/g, "").toUpperCase();
                                    const clean = raw.replace(/^#/, "");
                                    if (clean.length === 6) commitColor(clean, { keepOpen: true });
                                    else setLocal({ ...local, color: clean });
                                }}
                                placeholder="#RRGGBB"
                                className="flex-1 min-w-0 px-2 py-1.5 text-xs font-mono font-semibold text-slate-800 bg-slate-100 border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-slate-500 uppercase"
                                data-testid={isDraft ? "payables-draft-color-hex" : `payables-row-${row.id}-color-hex`}
                                aria-label="Hex colour code"
                            />
                        </div>
                        {palette.map((hx) => (
                            <button
                                key={hx}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    commitColor(hx);
                                }}
                                className={`w-7 h-7 rounded-sm border transition-transform hover:scale-110 ${(local.color || "").toUpperCase() === hx.toUpperCase() ? "ring-2 ring-slate-800 border-slate-800" : "border-slate-300"}`}
                                style={{ background: `#${hx}` }}
                                title={`#${hx}`}
                                data-testid={`payables-color-swatch-${hx}`}
                                aria-label={`Pick colour #${hx}`}
                            />
                        ))}
                        {/* iter-219 — explicit blank/no-colour sentinel.
                            Stores FFFFFF so _resolve_color returns it verbatim
                            (bypasses the deterministic hash fallback) and the
                            row renders with no tint. */}
                        <button
                            onClick={(e) => { e.stopPropagation(); commitColor("FFFFFF"); }}
                            className={`w-7 h-7 rounded-sm border-2 border-dashed transition-transform hover:scale-110 flex items-center justify-center ${(local.color || "").toUpperCase() === "FFFFFF" ? "ring-2 ring-slate-800 border-slate-800" : "border-slate-400 hover:border-slate-700"}`}
                            style={{ background: "white" }}
                            title="No colour (blank)"
                            data-testid={isDraft ? "payables-draft-color-blank" : `payables-row-${row.id}-color-blank`}
                            aria-label="No colour"
                        >
                            <X className="w-3.5 h-3.5 text-slate-500" />
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); commitColor(""); }}
                            className="col-span-4 mt-1 text-[11px] uppercase tracking-wider font-bold text-slate-500 hover:text-slate-900 py-1 border-t border-slate-200"
                            data-testid={isDraft ? "payables-draft-color-auto" : `payables-row-${row.id}-color-auto`}
                        >
                            Auto (by customer)
                        </button>
                    </div>,
                    document.body,
                )}
            </td>

            {/* Actions */}
            <td className="px-2 py-1.5 border border-slate-300 text-right whitespace-nowrap" data-label="">
                {isDraft ? (
                    <>
                        <button
                            onClick={onSave}
                            className="px-3 py-1 bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-bold rounded"
                            data-testid="payables-draft-save"
                        >
                            Save
                        </button>
                        <button
                            onClick={onCancel}
                            className="ml-1 p-1 text-slate-700 hover:text-slate-900"
                            data-testid="payables-draft-cancel"
                            aria-label="Cancel"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </>
                ) : saving ? (
                    <Loader2 className="w-4 h-4 animate-spin text-slate-700 inline" />
                ) : (
                    <button
                        onClick={onDelete}
                        className="p-1.5 rounded hover:bg-rose-500/20 text-rose-600"
                        data-testid={`payables-delete-${row.id}`}
                        aria-label="Delete row"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                )}
            </td>
        </tr>
    );
}

// iter-225 — Mirrors the "Job Summary" sheet in the user's reference
// workbook pixel-for-pixel: big title, subtitle, dark-blue 4472C4
// header row with white bold text, each category row tinted its
// resolved colour, and a 1F4E79 "GRAND TOTAL" row at the bottom.
function JobSummaryPanel({ summary, taxRate, setTaxRate }) {
    const { list, grand } = summary;
    const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return (
        <div
            className="rounded-md border border-slate-500 bg-white overflow-hidden mb-6 shadow-lg"
            data-testid="payables-job-summary"
        >
            {/* Title block — Excel row 1 + row 2 */}
            <div className="px-4 pt-3 pb-2 bg-white flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h3 className="text-lg font-bold text-slate-900 leading-tight">
                        Job Expense Summary
                    </h3>
                    <p className="text-[11px] text-slate-600 italic mt-0.5">
                        Subtotals calculated from total amounts (HST assumed at {taxRate}%)
                    </p>
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-700">
                    <span className="uppercase tracking-wider font-semibold">Tax rate</span>
                    <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.1}
                        value={taxRate}
                        onChange={(e) => setTaxRate(e.target.value === "" ? 0 : Number(e.target.value))}
                        className="w-16 px-2 py-1 text-right rounded bg-slate-100 border border-slate-400 text-slate-900 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        data-testid="payables-tax-rate"
                    />
                    <span>%</span>
                </label>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse" style={{ fontFamily: "'Calibri', 'Segoe UI', system-ui, sans-serif" }}>
                    {/* Excel row 4 — dark blue header, white bold text */}
                    <thead>
                        <tr style={{ background: "#4472C4", color: "#FFFFFF" }}>
                            <th className="text-left font-bold text-xs uppercase px-3 py-2.5 border border-slate-300">Job Category</th>
                            <th className="text-right font-bold text-xs uppercase px-3 py-2.5 border border-slate-300 w-20"># Entries</th>
                            <th className="text-right font-bold text-xs uppercase px-3 py-2.5 border border-slate-300 w-32">Subtotal (pre-HST)</th>
                            <th className="text-right font-bold text-xs uppercase px-3 py-2.5 border border-slate-300 w-28">HST ({taxRate}%)</th>
                            <th className="text-right font-bold text-xs uppercase px-3 py-2.5 border border-slate-300 w-32">Total</th>
                            <th className="text-left font-bold text-xs uppercase px-3 py-2.5 border border-slate-300">Notes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((g) => (
                            <tr
                                key={g.key}
                                style={{ background: `#${g.color || "FFFFFF"}`, color: "#111827" }}
                                data-testid={`payables-summary-row-${g.key}`}
                            >
                                <td className="px-3 py-1.5 font-semibold border border-slate-300">{g.key}</td>
                                <td className="px-3 py-1.5 text-right border border-slate-300">{g.count}</td>
                                <td className="px-3 py-1.5 text-right font-mono border border-slate-300">{money(g.subtotal)}</td>
                                <td className="px-3 py-1.5 text-right font-mono border border-slate-300">{money(g.tax)}</td>
                                <td className="px-3 py-1.5 text-right font-mono font-bold border border-slate-300">{money(g.total)}</td>
                                <td className="px-3 py-1.5 border border-slate-300" />
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        {/* Excel grand-total row — dark 1F4E79, white bold */}
                        <tr style={{ background: "#1F4E79", color: "#FFFFFF" }}>
                            <td className="px-3 py-2 font-bold uppercase tracking-wider text-xs border border-slate-300">Grand Total</td>
                            <td className="px-3 py-2 text-right font-bold border border-slate-300" data-testid="payables-summary-grand-count">{grand.count}</td>
                            <td className="px-3 py-2 text-right font-mono font-bold border border-slate-300" data-testid="payables-summary-grand-subtotal">{money(grand.subtotal)}</td>
                            <td className="px-3 py-2 text-right font-mono font-bold border border-slate-300" data-testid="payables-summary-grand-tax">{money(grand.tax)}</td>
                            <td className="px-3 py-2 text-right font-mono font-bold border border-slate-300" data-testid="payables-summary-grand-total">{money(grand.total)}</td>
                            <td className="px-3 py-2 border border-slate-300" />
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
}

// iter-218 — Live progress + per-file summary for the Batch Import flow.
// While `finished` is false the modal shows a progress bar + the
// currently-processing filename. After every file has been attempted,
// the modal shows the full breakdown: how many rows were created,
// which fields the AI couldn't find on each one, and any files that
// failed entirely.
// iter-226 — Styled delete-confirm modal. Portals to <body> so it
// escapes any `overflow:hidden` wrapper and lives above all other UI.
function ConfirmDeleteModal({ count, busy, onConfirm, onCancel }) {
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e) => {
            if (e.key === "Escape" && !busy) onCancel();
            if (e.key === "Enter" && !busy) onConfirm();
        };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onKey);
        };
    }, [onCancel, onConfirm, busy]);

    const label = count === 1 ? "this payable" : `${count} payables`;
    return createPortal(
        <div
            className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => !busy && onCancel()}
            data-testid="payables-delete-confirm"
        >
            <div
                className="w-full max-w-md rounded-lg bg-white shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                        <Trash2 className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                        <h3 className="text-base font-bold text-slate-900">
                            Delete {label}?
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            This can't be undone.
                        </p>
                    </div>
                </div>
                <div className="px-5 py-4 text-sm text-slate-700">
                    {count === 1
                        ? "The row — including any attached invoice files — will be permanently removed."
                        : `All ${count} selected rows — including any attached invoice files — will be permanently removed.`}
                </div>
                <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
                    <button
                        onClick={onCancel}
                        disabled={busy}
                        className="px-4 py-2 rounded-md text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-100 disabled:opacity-50"
                        data-testid="payables-delete-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={busy}
                        className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-red-600 hover:bg-red-500 disabled:opacity-50 flex items-center gap-2"
                        data-testid="payables-delete-confirm-btn"
                    >
                        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                        {busy ? "Deleting…" : "Delete"}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}

